// AdminTokenStore — per-room admin token persistence in localStorage.
//
// (architecture.md §18.0 多段リカバリ — Phase 4 範囲は admin_token を
//  発行 / 受領 / localStorage に保存するところまで. admin 認証 API を使う
//  のは Phase 6.)
//
// Design:
// - **キー設計**: `oekaki:adminToken:<roomId>` 形式. ルームごとに分離して
//   保存し、ルーム削除時に個別 clear 可能に. namespace prefix `oekaki:` は
//   将来 (Phase 6+) で他種 credential を localStorage に置く時に衝突回避.
// - **localStorage 不可環境** (private browsing で QuotaExceeded、Safari の
//   完全ブロック、SSR 等) では in-memory Map に fallback. 例外を投げない.
//   Phase 4 範囲では admin_token を実際に使うコード経路はないので、UX が
//   壊れなければよい (再入室時に「admin 機能が使えない」だけ).
// - **同期 API のみ** (localStorage は同期だし、in-memory も同期).
//
// Tests: AdminTokenStore.test.ts (happy-dom 環境で localStorage の round-trip /
// fallback / clear をカバー).

const KEY_PREFIX = 'oekaki:adminToken:';

const buildKey = (roomId: string): string => `${KEY_PREFIX}${roomId}`;

/**
 * Minimal Storage shape this module uses. `globalThis.localStorage` matches
 * this shape; tests can pass a mock with the same surface.
 *
 * `list()` 用に `key(i)` / `length` を追加で expose. browser `Storage` の API は
 * 標準でこれらを持っており、tests の wrap mock も同じ shape を提供する.
 * in-memory fallback は `Map` ベースの自前 enumerate で実装.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /**
   * Snapshot all stored keys. `Storage` の native API は `length` + `key(i)`
   * だが、それを 1 step に畳んだ helper. localStorage 直結時は内部で
   * `key(i)` を回す.
   *
   * **Optional** で、未実装の mock (= 旧テストの最小 mock) は無視される
   * → `list()` は空配列を返す. forward-compat のため.
   */
  keys?(): readonly string[];
}

export interface AdminTokenStore {
  /** Save the token for the given room. Idempotent. No-op if `token` is empty. */
  save(roomId: string, token: string): void;
  /** Look up the token for the given room. Returns `null` if absent. */
  get(roomId: string): string | null;
  /** Remove the token for the given room. Idempotent. */
  clear(roomId: string): void;
  /**
   * Snapshot all `(roomId, token)` pairs known to this store. Order is **not**
   * guaranteed (localStorage vs in-memory で異なる). Empty list if none.
   *
   * Phase 6+ で HomePage の「あなたのルーム」一覧 (= `POST /admin/rooms/by-tokens`
   * へ送る材料) を作るのに使う. localStorage 読み出し例外時は best-effort で
   * 空配列を返す (= UX を壊さない).
   */
  list(): readonly { roomId: string; token: string }[];
}

/**
 * Probe `globalThis.localStorage` and return it if usable, else `null`.
 *
 * Safari private browsing, QuotaExceeded, and similar failure modes show up
 * as exceptions on `setItem`. We test by writing then deleting a sentinel key.
 *
 * 戻り値には `keys()` を合成する: native `Storage` は `length` + `key(i)` を
 * 持つので、それを enumerate する形で `keys()` を提供 (= `list()` 用).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${KEY_PREFIX}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    // injected test fake が `keys()` を持つならそれを優先 (auto-probe test 用).
    const maybeKeys = (ls as Partial<StorageLike>).keys;
    if (typeof maybeKeys === 'function') {
      return ls as StorageLike;
    }
    return {
      getItem: (k) => ls.getItem(k),
      setItem: (k, v) => {
        ls.setItem(k, v);
      },
      removeItem: (k) => {
        ls.removeItem(k);
      },
      keys: () => {
        try {
          const out: string[] = [];
          const n = ls.length;
          for (let i = 0; i < n; i += 1) {
            const k = ls.key(i);
            if (k !== null) out.push(k);
          }
          return out;
        } catch {
          return [];
        }
      },
    };
  } catch {
    return null;
  }
};

/**
 * Build an in-memory fallback that satisfies `StorageLike`.
 */
const createMemoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
    keys: () => Array.from(map.keys()),
  };
};

export interface CreateAdminTokenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall back to in-memory if unavailable.
   */
  storage?: StorageLike | null;
}

/**
 * Construct an `AdminTokenStore`. Falls back to an in-memory map if the
 * passed (or auto-probed) storage rejects writes.
 */
export const createAdminTokenStore = (opts: CreateAdminTokenStoreOptions = {}): AdminTokenStore => {
  // `null` 明示は「fallback を強制 (e.g. テスト)」、`undefined` は auto-probe.
  const explicit = opts.storage;
  const storage =
    explicit === null
      ? createMemoryStorage()
      : (explicit ?? probeBrowserStorage() ?? createMemoryStorage());

  return {
    save(roomId, token) {
      if (token.length === 0) return;
      try {
        storage.setItem(buildKey(roomId), token);
      } catch {
        // Quota / disabled storage. Swallow — UX 上は Phase 6 まで影響なし.
      }
    },
    get(roomId) {
      try {
        return storage.getItem(buildKey(roomId));
      } catch {
        return null;
      }
    },
    clear(roomId) {
      try {
        storage.removeItem(buildKey(roomId));
      } catch {
        // ignore
      }
    },
    list() {
      // `keys()` 未実装 (= 旧最小 mock) なら空配列で fail-silent.
      const keysFn = storage.keys;
      if (typeof keysFn !== 'function') return [];
      let keys: readonly string[];
      try {
        keys = keysFn.call(storage);
      } catch {
        return [];
      }
      const out: { roomId: string; token: string }[] = [];
      for (const k of keys) {
        if (!k.startsWith(KEY_PREFIX)) continue;
        const roomId = k.slice(KEY_PREFIX.length);
        // sentinel (`__probe__`) や empty suffix は除外.
        if (roomId.length === 0 || roomId.startsWith('__')) continue;
        let token: string | null;
        try {
          token = storage.getItem(k);
        } catch {
          continue;
        }
        if (token === null || token.length === 0) continue;
        out.push({ roomId, token });
      }
      return out;
    },
  };
};
